import { useRef } from "react";
import { cancelAllOrders, cancelOrder, errorText } from "./api";
import { useIbkrData, useVisible } from "./poller";
import { OrderInfo } from "./types";
import { C, Chip, IconButton, IntroHeader, SideTag, WidgetBox, fmt, freshnessDot } from "./ui";
import { WidgetProps, closeModifyWindows, openLadder, openModify } from "./windows";

// Open orders plus this client's recently filled ones (filled rows have no
// buttons); a cancelled order leaves the list together with its modify
// windows. Orders placed by other clients (TWS, ...) can't be changed here,
// so they get no buttons either.
export function OrdersWidget({ widget, actions, intro }: WidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useVisible(ref);
  const { data, freshness, error } = useIbkrData<OrderInfo[]>("orders", {}, visible);
  // in the params, not component state: the chat snapshot sent to the agent includes it
  const actionError: string | null = widget.params.error ?? null;
  const setActionError = (e: string | null) => actions.patch({ error: e ?? undefined });
  const orders = (data ?? []).filter((o) => o.status !== "cancelled" && o.status !== "rejected");

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      setActionError(null);
    } catch (e) {
      setActionError(errorText(e));
    }
  };

  const cancel = (o: OrderInfo) =>
    run(async () => {
      await cancelOrder(o.orderId);
      closeModifyWindows(actions, o.orderId);
    });

  const cancelAll = () =>
    run(async () => {
      const cancelled = await cancelAllOrders();
      cancelled.forEach((o) => closeModifyWindows(actions, o.orderId));
    });

  return (
    <>
      <IntroHeader stampLine={intro} text={`${orders.length} active orders. Press the pencil to modify an order.`} />
      <WidgetBox boxRef={ref} headerAlign="center" title={<span style={{ fontWeight: 600 }}>Active orders ({orders.length})</span>} status={freshnessDot(freshness)} onDuplicate={() => actions.duplicate({ ...widget.params })} onClose={actions.close}>
        {orders.length === 0 && (
          <div style={{ padding: "8px 10px", color: C.muted, borderBottom: `1px solid ${C.divider}` }}>{error ?? (data ? "No active orders." : "Loading…")}</div>
        )}
        {orders.map((o) => {
          const live = o.filled < o.size && o.status !== "filled";
          return (
            <div key={`${o.clientId}-${o.orderId}-${o.permId}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderBottom: `1px solid ${C.divider}` }}>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 13 }}>
                  <SideTag side={o.side} /> <Chip style={{ whiteSpace: "nowrap" }}>{o.contract.label}</Chip> {o.size} @ {o.price != null ? fmt(o.price) : o.orderType}{o.ladder ? " (Ladder)" : ""}
                </span>
                <span style={{ color: C.muted }}>
                  Filled {o.filled} / {o.size}{!o.own ? ` · ${o.clientId === 0 ? "TWS" : `client ${o.clientId}`}` : ""}
                </span>
              </div>
              {live && o.own ? (
                <>
                  <IconButton icon="ladder" label={`Ladder buy / sell for ${o.contract.label}`} title="Ladder buy / sell"
                    onClick={() => openLadder(actions, o.contract, o.size)} />
                  <IconButton icon="pencil" label={`Modify ${o.contract.label} ${o.side.toLowerCase()} order`} title="Modify order"
                    onClick={() => openModify(actions, o)} />
                  <IconButton icon="close" label={`Cancel ${o.contract.label} ${o.side.toLowerCase()} order`} title="Cancel order"
                    style={{ color: C.sell, borderColor: C.sell }} onClick={() => cancel(o)} />
                </>
              ) : (
                <div style={{ flex: "none", width: 128, height: 36 }} />
              )}
            </div>
          );
        })}
        <div style={{ padding: 10, background: C.headerBg, display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={cancelAll} style={{ height: 36, padding: "0 16px", background: C.sell, color: "#fff", border: 0, fontSize: 12.5 }}>
            Cancel all orders
          </button>
          {actionError && <span style={{ color: C.cancelled, fontSize: 11.5 }}>{actionError}</span>}
        </div>
      </WidgetBox>
    </>
  );
}
